import { ObjectId } from "bson";
import { Brand, Schema } from "effect";

import { BufferFromDb } from "../schema/helpers";
import { toRawPrefixedMongoId } from "./mongo-id-format";

// Named types for Mongo ID schemas - prevents TypeScript from expanding full structural
// types in declaration files, significantly reducing .d.ts size and compile time.
// See: https://github.com/microsoft/TypeScript/wiki/Performance#preferring-interfaces-over-intersections

export type PrefixedMongoId<T extends string> = string & Brand.Brand<T>;

export type PrefixedMongoIdFromDb<T extends string> = Schema.transform<
  Schema.Union<
    [Schema.instanceOf<ObjectId>, typeof Schema.String, typeof Schema.Object]
  >,
  Schema.brand<Schema.filter<typeof Schema.String>, T>
>;

export type PrefixedMongoIdFromDbBuffer<T extends string> = Schema.transform<
  Schema.declare<Buffer, Buffer, readonly [], never>,
  Schema.brand<Schema.filter<typeof Schema.String>, T>
>;

export interface PrefixedMongoIdSchema<T extends string> extends Schema.brand<
  Schema.filter<typeof Schema.String>,
  T
> {
  readonly make: (id: string) => PrefixedMongoId<T>;
  readonly makeNew: () => PrefixedMongoId<T>;
  readonly fromObjectId: (id: ObjectId) => PrefixedMongoId<T>;
  readonly fromString: (id: string) => PrefixedMongoId<T>;
  readonly toObjectId: (id: PrefixedMongoId<T>) => ObjectId;
  readonly toString: (id: PrefixedMongoId<T>) => string;
  /**
   * Raw 24-hex ObjectId for an id that may arrive either prefixed
   * (`m_xx_<hex>`) or already raw. Tolerant counterpart to `toString`, which
   * assumes the prefixed form. Useful when comparing against raw ObjectId
   * constants regardless of which form the caller holds.
   */
  readonly toRawId: (id: string) => string;
  readonly toBuffer: (id: PrefixedMongoId<T>) => Buffer;
  readonly fromBuffer: (buf: Buffer) => PrefixedMongoId<T>;
  readonly fromDb: PrefixedMongoIdFromDb<T>;
  readonly fromDbBuffer: PrefixedMongoIdFromDbBuffer<T>;
  /** Whether `value` is a well-formed ID of this type, prefix and hex body. */
  readonly is: (value: string) => value is PrefixedMongoId<T>;
  readonly prefix: string;
}

export const MongoIdPrefix = {
  Team: "tm",
  User: "us",
  Customer: "cust",
  LlmThread: "llm",
  CallRecord: "call",
  Conversation: "conv",
  Order: "ord",
  Tag: "tag",
  Rule: "rule",
} as const;
type MongoIdPrefixKey = keyof typeof MongoIdPrefix;

const prefixes = Object.values(MongoIdPrefix);
const prefixSet = new Set(prefixes);
if (prefixes.length !== prefixSet.size) {
  throw new Error("MongoIdPrefix values must be unique");
}

const MONGO_PREFIX = "m_";
const HEX_PATTERN = "[a-f0-9]{24}";

export function createPrefixedMongoIdSchema<T extends MongoIdPrefixKey>(
  typeName: T,
  prefix: (typeof MongoIdPrefix)[T],
): PrefixedMongoIdSchema<T> {
  const fullPrefix = `${MONGO_PREFIX}${prefix}`;
  const regex = new RegExp(`^${fullPrefix}_${HEX_PATTERN}$`);

  const baseSchema = Schema.String.pipe(
    Schema.pattern(regex, {
      message: () => `${typeName} must match ${fullPrefix}_<24 hex chars>`,
    }),
    Schema.brand(typeName),
  );

  type IdType = PrefixedMongoId<T>;

  /** Derived once: `Schema.decodeSync` compiles a parser, and `make` is hot. */
  const decode = Schema.decodeSync(baseSchema);
  const make = (id: string): IdType => decode(id) as IdType;

  const is = (value: string): value is IdType => regex.test(value);

  const fromObjectId = (id: ObjectId): IdType =>
    make(`${fullPrefix}_${id.toHexString()}`);

  const fromString = (id: string): IdType => fromObjectId(new ObjectId(id));

  const toObjectId = (id: IdType): ObjectId =>
    new ObjectId(id.slice(fullPrefix.length + 1));

  const toString = (id: IdType): string => id.slice(fullPrefix.length + 1);

  const toRawId = (id: string): string => toRawPrefixedMongoId(id, fullPrefix);

  const fromDb: PrefixedMongoIdFromDb<T> = Schema.transform(
    Schema.Union(Schema.instanceOf(ObjectId), Schema.String, Schema.Object),
    baseSchema,
    {
      strict: true,
      decode: (value): IdType => {
        if (value instanceof ObjectId) {
          return fromObjectId(value);
        }
        if (typeof value === "string") {
          // CRDB serializes BYTES inside a `json_build_object(...)` subquery
          // as `\x<24-hex-chars>`. Strip the prefix so `new ObjectId(hex)`
          // accepts the canonical 24-char hex form.
          const raw =
            value.startsWith("\\x") || value.startsWith("\\X")
              ? value.slice(2)
              : value;
          return fromObjectId(new ObjectId(raw));
        }
        // Handle plain object (from .lean()) - convert to ObjectId first
        if (
          typeof value === "object" &&
          value !== null &&
          typeof value.toString === "function"
        ) {
          return fromObjectId(new ObjectId(value.toString()));
        }
        throw new Error(`Invalid value for ${typeName}: ${value}`);
      },
      encode: (id): ObjectId => toObjectId(id as IdType),
    },
  ) as PrefixedMongoIdFromDb<T>;

  const toBuffer = (id: IdType): Buffer => Buffer.from(toObjectId(id).id);

  const fromBuffer = (buf: Buffer): IdType => fromObjectId(new ObjectId(buf));

  const makeNew = (): IdType => fromObjectId(new ObjectId());

  const fromDbBuffer: PrefixedMongoIdFromDbBuffer<T> = Schema.transform(
    BufferFromDb,
    baseSchema,
    {
      strict: true,
      decode: (buf): IdType => fromBuffer(buf),
      encode: (id): Buffer => toBuffer(id as IdType),
    },
  ) as PrefixedMongoIdFromDbBuffer<T>;

  return Object.assign(baseSchema, {
    make,
    makeNew,
    fromObjectId,
    fromString,
    toObjectId,
    toString,
    toRawId,
    toBuffer,
    fromBuffer,
    fromDb,
    fromDbBuffer,
    is,
    prefix: fullPrefix,
  }) as PrefixedMongoIdSchema<T>;
}

export const MongoTeamId = createPrefixedMongoIdSchema(
  "Team",
  MongoIdPrefix.Team,
);
export type MongoTeamId = typeof MongoTeamId.Type;

export const MongoUserId = createPrefixedMongoIdSchema(
  "User",
  MongoIdPrefix.User,
);
export type MongoUserId = typeof MongoUserId.Type;

export const MongoCustomerId = createPrefixedMongoIdSchema(
  "Customer",
  MongoIdPrefix.Customer,
);
export type MongoCustomerId = typeof MongoCustomerId.Type;

export const MongoLlmThreadId = createPrefixedMongoIdSchema(
  "LlmThread",
  MongoIdPrefix.LlmThread,
);
export type MongoLlmThreadId = typeof MongoLlmThreadId.Type;

export const MongoCallRecordId = createPrefixedMongoIdSchema(
  "CallRecord",
  MongoIdPrefix.CallRecord,
);
export type MongoCallRecordId = typeof MongoCallRecordId.Type;

export const MongoConversationId = createPrefixedMongoIdSchema(
  "Conversation",
  MongoIdPrefix.Conversation,
);
export type MongoConversationId = typeof MongoConversationId.Type;

export const MongoOrderId = createPrefixedMongoIdSchema(
  "Order",
  MongoIdPrefix.Order,
);
export type MongoOrderId = typeof MongoOrderId.Type;

export const MongoTagId = createPrefixedMongoIdSchema("Tag", MongoIdPrefix.Tag);
export type MongoTagId = typeof MongoTagId.Type;

export const MongoRuleId = createPrefixedMongoIdSchema(
  "Rule",
  MongoIdPrefix.Rule,
);
export type MongoRuleId = typeof MongoRuleId.Type;
